import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Flex, Heading, Text, Input, InputGroup, InputLeftElement,
  SimpleGrid, Badge, Button, IconButton, useColorModeValue, Spinner,
  Menu, MenuButton, MenuList, MenuItem, Portal, Image, AspectRatio,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Tabs, TabList, Tab, TabPanels, TabPanel, Tooltip, Stack, HStack, VStack
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFilter, faSortAmountDown, faThLarge, faList,
  faVideo, faImage, faFilePdf, faMusic, faEye, faFolderOpen,
  faHardDrive, faLayerGroup, faArrowUpRightFromSquare, faTag
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { apiGetRequest } from '@/functions/requests';
import { useS3Client } from '@/hooks/contextHooks';
import { GetObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { AppMeta } from '@xnav/types/src/common';
import Loading from '@/components/loading';

enum MediaType {
  Video = 0,
  Audio = 1,
  Pdf = 2,
  Image = 3
}

type GlobalMediaItem = {
  id: string;
  name: string;
  type: MediaType;
  size: number;
  uploaded: string | Date;
  fileUrl?: string;
  iconUrl?: string;
  courseId: string;
  courseName: string;
  courseCreator: string;
  courseOrg?: string;
  references?: string[];
  tags?: string[];
};

function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

const MotionBox = motion(Box);

export default function MediaHub() {
  const navigate = useNavigate();
  const s3 = useS3Client() as S3Client;
  const [loading, setLoading] = useState(true);
  const [mediaItems, setMediaItems] = useState<GlobalMediaItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<number | 'all'>('all');
  const [selectedOrg, setSelectedOrg] = useState<string>('All');
  const [selectedCourse, setSelectedCourse] = useState<string>('All');
  const [sortBy, setSortBy] = useState<'newest' | 'size' | 'name'>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Preview Modal
  const [previewItem, setPreviewItem] = useState<GlobalMediaItem | null>(null);

  const glassBg = useColorModeValue('rgba(255, 255, 255, 0.85)', 'rgba(26, 32, 44, 0.85)');
  const glassBorder = useColorModeValue('1px solid rgba(226, 232, 240, 0.8)', '1px solid rgba(45, 55, 72, 0.8)');
  const titleColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    fetchGlobalMedia();
  }, [s3]);

  const fetchGlobalMedia = async () => {
    setLoading(true);
    try {
      const apps = await apiGetRequest<undefined, AppMeta[]>('/apps');
      if (!apps || apps.length === 0) {
        setMediaItems([]);
        setLoading(false);
        return;
      }

      const allItems: GlobalMediaItem[] = [];

      await Promise.all(
        apps.map(async (app) => {
          try {
            let metaList: any[] | null = null;
            if (s3) {
              try {
                const metaCmd = new GetObjectCommand({
                  Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
                  Key: `${app.id}/media/meta.json`
                });
                const metaUrl = await getSignedUrl(s3 as any, metaCmd as any, { expiresIn: 3600 });
                const res = await fetch(metaUrl);
                if (res.ok) {
                  metaList = await res.json();
                }
              } catch (e) {}
            }
            if (!metaList) {
              metaList = await apiGetRequest<string, any[]>(`/media/${app.id}`);
            }

            if (Array.isArray(metaList)) {
              for (const m of metaList) {
                let fileUrl: string | undefined = undefined;
                let iconUrl: string | undefined = undefined;

                if (s3) {
                  try {
                    const fileCmd = new GetObjectCommand({
                      Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
                      Key: `${app.id}/media/media/${m.id}`
                    });
                    fileUrl = await getSignedUrl(s3 as any, fileCmd as any, { expiresIn: 3600 });

                    if (m.iconId) {
                      const iconCmd = new GetObjectCommand({
                        Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
                        Key: `${app.id}/media/icons/${m.iconId}`
                      });
                      iconUrl = await getSignedUrl(s3 as any, iconCmd as any, { expiresIn: 3600 });
                    }
                  } catch (e) {}
                }

                allItems.push({
                  id: m.id,
                  name: m.name || 'Untitled File',
                  type: typeof m.type === 'number' ? m.type : m.type === 'video' ? 0 : m.type === 'image' ? 3 : m.type === 'pdf' ? 2 : 1,
                  size: m.size || 0,
                  uploaded: m.uploaded || new Date(),
                  fileUrl,
                  iconUrl,
                  courseId: app.id,
                  courseName: app.name,
                  courseCreator: app.creator || 'Instructor',
                  courseOrg: app.org,
                  references: m.references || [],
                  tags: m.tags || []
                });
              }
            }
          } catch (e) {
            console.error(`Error loading media for app ${app.name}`, e);
          }
        })
      );

      setMediaItems(allItems);
    } catch (e) {
      console.error('Error fetching global media assets', e);
    } finally {
      setLoading(false);
    }
  };

  const uniqueOrgs = useMemo(() => {
    const set = new Set<string>();
    mediaItems.forEach((m) => {
      if (m.courseOrg) set.add(m.courseOrg);
    });
    return Array.from(set).sort();
  }, [mediaItems]);

  const uniqueCourses = useMemo(() => {
    const map = new Map<string, string>();
    mediaItems.forEach((m) => {
      map.set(m.courseId, m.courseName);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [mediaItems]);

  // Overall Statistics
  const stats = useMemo(() => {
    let totalVideos = 0;
    let totalPhotos = 0;
    let totalPdfs = 0;
    let totalAudio = 0;
    let totalSize = 0;

    mediaItems.forEach((m) => {
      totalSize += m.size;
      if (m.type === MediaType.Video) totalVideos++;
      else if (m.type === MediaType.Image) totalPhotos++;
      else if (m.type === MediaType.Pdf) totalPdfs++;
      else if (m.type === MediaType.Audio) totalAudio++;
    });

    return {
      totalAssets: mediaItems.length,
      totalVideos,
      totalPhotos,
      totalPdfs,
      totalAudio,
      totalSize: formatBytes(totalSize)
    };
  }, [mediaItems]);

  // Filtered & Sorted List
  const filteredMedia = useMemo(() => {
    let items = [...mediaItems];

    if (selectedType !== 'all') {
      items = items.filter((m) => m.type === selectedType);
    }

    if (selectedOrg !== 'All') {
      items = items.filter((m) => m.courseOrg?.toLowerCase() === selectedOrg.toLowerCase());
    }

    if (selectedCourse !== 'All') {
      items = items.filter((m) => m.courseId === selectedCourse);
    }

    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.courseName.toLowerCase().includes(q) ||
          m.courseCreator.toLowerCase().includes(q) ||
          (m.tags && m.tags.some((t) => t.toLowerCase().includes(q)))
      );
    }

    items.sort((a, b) => {
      if (sortBy === 'size') return b.size - a.size;
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return new Date(b.uploaded).getTime() - new Date(a.uploaded).getTime();
    });

    return items;
  }, [mediaItems, selectedType, selectedOrg, selectedCourse, searchQuery, sortBy]);

  const getTypeIcon = (type: MediaType) => {
    switch (type) {
      case MediaType.Video:
        return { icon: faVideo, color: 'purple.500', bg: 'purple.50', label: 'Video' };
      case MediaType.Image:
        return { icon: faImage, color: 'teal.500', bg: 'teal.50', label: 'Photo' };
      case MediaType.Pdf:
        return { icon: faFilePdf, color: 'red.500', bg: 'red.50', label: 'PDF' };
      default:
        return { icon: faMusic, color: 'blue.500', bg: 'blue.50', label: 'Audio' };
    }
  };

  if (loading) return <Loading message="Aggregating global asset library..." />;

  return (
    <Flex flexDir="column" w="100%" maxW={{ base: "100%", "2xl": "1600px" }} mx="auto" p={{ base: 3, md: 5 }}>
      {/* Header Banner */}
      <Flex
        w="100%"
        p={6}
        bgGradient="linear(to-r, #0284c7, #0ea5e9, #06b6d4, #10b981)"
        borderRadius="2xl"
        mb={8}
        alignItems="center"
        justifyContent="space-between"
        color="white"
        boxShadow="0 12px 32px rgba(14, 165, 233, 0.25)"
        position="relative"
        overflow="hidden"
        flexWrap="wrap"
        gap={4}
      >
        <Box position="absolute" top="-30%" left="-10%" w="120%" h="160%" bgGradient="radial(circle, rgba(255,255,255,0.2) 0%, transparent 60%)" pointerEvents="none" />
        <Box zIndex={2}>
          <Heading size="lg" fontWeight="black" letterSpacing="tight" mb={1}>
            Global Asset & Media Hub
          </Heading>
          <Text fontSize="sm" opacity={0.9}>
            Centralized library of all ultrasound loops, medical videos, diagrams, and PDFs across all courses.
          </Text>
        </Box>

        {/* Quick Stats Pill */}
        <HStack spacing={3} zIndex={2} flexWrap="wrap">
          <Flex bg="whiteAlpha.200" backdropFilter="blur(10px)" px={4} py={2} borderRadius="xl" align="center" gap={2}>
            <FontAwesomeIcon icon={faHardDrive} />
            <Text fontWeight="bold" fontSize="sm">{stats.totalSize}</Text>
          </Flex>
          <Flex bg="whiteAlpha.200" backdropFilter="blur(10px)" px={4} py={2} borderRadius="xl" align="center" gap={2}>
            <FontAwesomeIcon icon={faLayerGroup} />
            <Text fontWeight="bold" fontSize="sm">{stats.totalAssets} Total Assets</Text>
          </Flex>
        </HStack>
      </Flex>

      {/* Stats Summary Cards */}
      <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={4} mb={6}>
        <Flex p={4} bg={glassBg} borderRadius="xl" border={glassBorder} backdropFilter="blur(20px)" align="center" gap={3.5} shadow="sm">
          <Flex w="42px" h="42px" borderRadius="xl" bg="purple.50" color="purple.500" align="center" justify="center" fontSize="lg">
            <FontAwesomeIcon icon={faVideo} />
          </Flex>
          <Box>
            <Text fontSize="2xl" fontWeight="black" color={titleColor} lineHeight="1">{stats.totalVideos}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" mt={1}>Videos / Clips</Text>
          </Box>
        </Flex>

        <Flex p={4} bg={glassBg} borderRadius="xl" border={glassBorder} backdropFilter="blur(20px)" align="center" gap={3.5} shadow="sm">
          <Flex w="42px" h="42px" borderRadius="xl" bg="teal.50" color="teal.500" align="center" justify="center" fontSize="lg">
            <FontAwesomeIcon icon={faImage} />
          </Flex>
          <Box>
            <Text fontSize="2xl" fontWeight="black" color={titleColor} lineHeight="1">{stats.totalPhotos}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" mt={1}>Photos / Diagrams</Text>
          </Box>
        </Flex>

        <Flex p={4} bg={glassBg} borderRadius="xl" border={glassBorder} backdropFilter="blur(20px)" align="center" gap={3.5} shadow="sm">
          <Flex w="42px" h="42px" borderRadius="xl" bg="red.50" color="red.500" align="center" justify="center" fontSize="lg">
            <FontAwesomeIcon icon={faFilePdf} />
          </Flex>
          <Box>
            <Text fontSize="2xl" fontWeight="black" color={titleColor} lineHeight="1">{stats.totalPdfs}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" mt={1}>PDF Documents</Text>
          </Box>
        </Flex>

        <Flex p={4} bg={glassBg} borderRadius="xl" border={glassBorder} backdropFilter="blur(20px)" align="center" gap={3.5} shadow="sm">
          <Flex w="42px" h="42px" borderRadius="xl" bg="green.50" color="green.500" align="center" justify="center" fontSize="lg">
            <FontAwesomeIcon icon={faMusic} />
          </Flex>
          <Box>
            <Text fontSize="2xl" fontWeight="black" color={titleColor} lineHeight="1">{stats.totalAudio}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.400" mt={1}>Audio Tracks</Text>
          </Box>
        </Flex>
      </SimpleGrid>

      {/* Filter and Search Bar */}
      <Flex
        w="100%"
        bg={useColorModeValue('rgba(255, 255, 255, 0.75)', 'rgba(26, 32, 44, 0.75)')}
        backdropFilter="blur(20px)"
        p={2}
        pl={4}
        borderRadius="full"
        boxShadow={useColorModeValue('0 8px 32px 0 rgba(31, 38, 135, 0.05)', '0 8px 32px 0 rgba(0, 0, 0, 0.2)')}
        border="1px solid"
        borderColor={useColorModeValue('rgba(226, 232, 240, 0.8)', 'rgba(45, 55, 72, 0.8)')}
        mb={6}
        flexDir={{ base: 'column', md: 'row' }}
        alignItems="center"
        gap={2}
        position="relative"
        zIndex={2}
      >
        {/* Search Input */}
        <InputGroup size="lg" flex={1}>
          <InputLeftElement pointerEvents="none" h="full" color="green.400" pl={2}>
            <FontAwesomeIcon icon={faSearch} />
          </InputLeftElement>
          <Input
            placeholder="Search assets by name, tag, or course..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="transparent"
            border="none"
            _focus={{ boxShadow: 'none' }}
            fontSize="md"
            fontWeight="600"
            pl={10}
          />
        </InputGroup>

        <Flex gap={2} w={{ base: '100%', md: 'auto' }} flexWrap="wrap" alignItems="center" pr={2}>
          {/* Org Filter */}
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              borderRadius="full"
              leftIcon={<FontAwesomeIcon icon={faFilter} color="#0ea5e9" />}
              fontWeight="700"
              fontSize="sm"
            >
              {selectedOrg === 'All' ? 'All Organizations' : selectedOrg}
            </MenuButton>
            <Portal>
              <MenuList shadow="xl" borderRadius="xl" border="none" bg={useColorModeValue('white', 'gray.800')} p={2} zIndex={1500}>
                <MenuItem py={2.5} borderRadius="lg" fontWeight={selectedOrg === 'All' ? 'bold' : 'normal'} onClick={() => setSelectedOrg('All')}>
                  All Organizations
                </MenuItem>
                {uniqueOrgs.map((org) => (
                  <MenuItem key={org} py={2.5} borderRadius="lg" fontWeight={selectedOrg === org ? 'bold' : 'normal'} onClick={() => setSelectedOrg(org)}>
                    {org}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>

          {/* Course Filter */}
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              borderRadius="full"
              leftIcon={<FontAwesomeIcon icon={faFolderOpen} color="#0ea5e9" />}
              fontWeight="700"
              fontSize="sm"
            >
              {selectedCourse === 'All' ? 'All Courses' : uniqueCourses.find((c) => c.id === selectedCourse)?.name || 'Course'}
            </MenuButton>
            <Portal>
              <MenuList shadow="xl" borderRadius="xl" border="none" maxH="300px" overflowY="auto" bg={useColorModeValue('white', 'gray.800')} p={2} zIndex={1500}>
                <MenuItem py={2.5} borderRadius="lg" fontWeight={selectedCourse === 'All' ? 'bold' : 'normal'} onClick={() => setSelectedCourse('All')}>
                  All Courses
                </MenuItem>
                {uniqueCourses.map((c) => (
                  <MenuItem key={c.id} py={2.5} borderRadius="lg" fontWeight={selectedCourse === c.id ? 'bold' : 'normal'} onClick={() => setSelectedCourse(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>

          <Box w="1px" h="20px" bg={useColorModeValue('gray.200', 'gray.700')} mx={1} display={{ base: 'none', md: 'block' }} />

          {/* Sort Menu */}
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              borderRadius="full"
              leftIcon={<FontAwesomeIcon icon={faSortAmountDown} color="#0ea5e9" />}
              fontWeight="700"
              fontSize="sm"
            >
              Sort: {sortBy === 'newest' ? 'Newest' : sortBy === 'size' ? 'File Size' : 'Name'}
            </MenuButton>
            <Portal>
              <MenuList shadow="xl" borderRadius="xl" border="none" bg={useColorModeValue('white', 'gray.800')} p={2} zIndex={1500}>
                <MenuItem py={2.5} borderRadius="lg" fontWeight={sortBy === 'newest' ? 'bold' : 'normal'} onClick={() => setSortBy('newest')}>
                  Newest Uploads
                </MenuItem>
                <MenuItem py={2.5} borderRadius="lg" fontWeight={sortBy === 'size' ? 'bold' : 'normal'} onClick={() => setSortBy('size')}>
                  Largest File Size
                </MenuItem>
                <MenuItem py={2.5} borderRadius="lg" fontWeight={sortBy === 'name' ? 'bold' : 'normal'} onClick={() => setSortBy('name')}>
                  File Name (A-Z)
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>

          <Box w="1px" h="20px" bg={useColorModeValue('gray.200', 'gray.700')} mx={1} display={{ base: 'none', md: 'block' }} />

          {/* View Toggle */}
          <Flex bg={useColorModeValue('gray.100', 'gray.900')} p={1} borderRadius="full" border="1px solid" borderColor={useColorModeValue('gray.200', 'gray.800')}>
            <IconButton
              aria-label="Grid View"
              icon={<FontAwesomeIcon icon={faThLarge} />}
              onClick={() => setViewMode('grid')}
              variant={viewMode === 'grid' ? 'solid' : 'ghost'}
              colorScheme="green"
              bg={viewMode === 'grid' ? 'green.500' : 'transparent'}
              color={viewMode === 'grid' ? 'white' : 'gray.500'}
              borderRadius="full"
              size="sm"
            />
            <IconButton
              aria-label="List View"
              icon={<FontAwesomeIcon icon={faList} />}
              onClick={() => setViewMode('list')}
              variant={viewMode === 'list' ? 'solid' : 'ghost'}
              colorScheme="green"
              bg={viewMode === 'list' ? 'green.500' : 'transparent'}
              color={viewMode === 'list' ? 'white' : 'gray.500'}
              borderRadius="full"
              size="sm"
            />
          </Flex>
        </Flex>
      </Flex>

      {/* Asset Type Tabs */}
      <Flex mb={6} justify="flex-start">
        <Tabs variant="soft-rounded" colorScheme="green" index={selectedType === 'all' ? 0 : selectedType === MediaType.Video ? 1 : selectedType === MediaType.Image ? 2 : selectedType === MediaType.Pdf ? 3 : 4} onChange={(idx) => {
          if (idx === 0) setSelectedType('all');
          else if (idx === 1) setSelectedType(MediaType.Video);
          else if (idx === 2) setSelectedType(MediaType.Image);
          else if (idx === 3) setSelectedType(MediaType.Pdf);
          else if (idx === 4) setSelectedType(MediaType.Audio);
        }}>
          <TabList bg={useColorModeValue('white', 'gray.900')} p={1} borderRadius="full" border="1px solid" borderColor={useColorModeValue('gray.200', 'gray.800')} gap={1}>
            <Tab fontWeight="bold" fontSize="xs" px={4} py={2} borderRadius="full">All Assets ({mediaItems.length})</Tab>
            <Tab fontWeight="bold" fontSize="xs" px={4} py={2} borderRadius="full">Videos ({stats.totalVideos})</Tab>
            <Tab fontWeight="bold" fontSize="xs" px={4} py={2} borderRadius="full">Photos ({stats.totalPhotos})</Tab>
            <Tab fontWeight="bold" fontSize="xs" px={4} py={2} borderRadius="full">PDFs ({stats.totalPdfs})</Tab>
            <Tab fontWeight="bold" fontSize="xs" px={4} py={2} borderRadius="full">Audio ({stats.totalAudio})</Tab>
          </TabList>
        </Tabs>
      </Flex>

      {/* Media Items List / Grid */}
      {filteredMedia.length === 0 ? (
        <Flex p={12} bg={glassBg} borderRadius="2xl" border={glassBorder} direction="column" align="center" justify="center" textAlign="center">
          <Flex w="56px" h="56px" bg="green.50" color="green.500" borderRadius="full" align="center" justify="center" fontSize="xl" mb={3}>
            <FontAwesomeIcon icon={faSearch} />
          </Flex>
          <Heading size="md" color={titleColor} mb={1}>No assets found</Heading>
          <Text fontSize="sm" color="gray.500">Try adjusting your filters or search terms.</Text>
        </Flex>
      ) : viewMode === 'grid' ? (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={5}>
          {filteredMedia.map((item) => {
            const typeInfo = getTypeIcon(item.type);
            return (
              <MotionBox
                key={`${item.courseId}-${item.id}`}
                bg={glassBg}
                borderRadius="2xl"
                border={glassBorder}
                backdropFilter="blur(20px)"
                overflow="hidden"
                shadow="sm"
                whileHover={{ y: -4, shadow: 'xl' }}
                transition={{ duration: 0.2 }}
                display="flex"
                flexDir="column"
              >
                {/* Thumbnail Preview Area */}
                <Box position="relative" bg={useColorModeValue('gray.100', 'gray.900')} h="140px" display="flex" alignItems="center" justifyContent="center" overflow="hidden" cursor="pointer" onClick={() => setPreviewItem(item)}>
                  {item.type === MediaType.Image && item.fileUrl ? (
                    <Image src={item.fileUrl} alt={item.name} w="100%" h="100%" objectFit="cover" />
                  ) : item.type === MediaType.Video && item.iconUrl ? (
                    <Image src={item.iconUrl} alt={item.name} w="100%" h="100%" objectFit="cover" />
                  ) : (
                    <Flex align="center" justify="center" direction="column" gap={2} color={typeInfo.color}>
                      <FontAwesomeIcon icon={typeInfo.icon} size="2x" />
                      <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase">{typeInfo.label}</Text>
                    </Flex>
                  )}

                  {/* Type Badge */}
                  <Badge position="absolute" top={2.5} left={2.5} colorScheme={item.type === MediaType.Video ? 'purple' : item.type === MediaType.Image ? 'teal' : item.type === MediaType.Pdf ? 'red' : 'green'} borderRadius="full" px={2} py={0.5} fontSize="2xs" fontWeight="bold">
                    <FontAwesomeIcon icon={typeInfo.icon} style={{ marginRight: '4px' }} />
                    {typeInfo.label}
                  </Badge>

                  {/* Size Pill */}
                  <Badge position="absolute" bottom={2.5} right={2.5} bg="blackAlpha.700" color="white" borderRadius="md" px={1.5} py={0.5} fontSize="2xs" fontWeight="bold">
                    {formatBytes(item.size)}
                  </Badge>
                </Box>

                {/* Info Area */}
                <Box p={4} flex={1} display="flex" flexDir="column" justifyContent="space-between">
                  <Box mb={3}>
                    <Heading size="xs" color={titleColor} fontWeight="bold" noOfLines={2} title={item.name} mb={1.5}>
                      {item.name}
                    </Heading>
                    
                    {/* Course Pill */}
                    <Tooltip label={`Instructor: ${item.courseCreator}`} placement="top" hasArrow>
                      <Flex
                        align="center"
                        gap={1.5}
                        bg={useColorModeValue('gray.50', 'gray.850')}
                        p={1.5}
                        borderRadius="lg"
                        cursor="pointer"
                        _hover={{ bg: useColorModeValue('teal.50', 'teal.950/40'), color: 'teal.500' }}
                        onClick={() => navigate(`/${item.courseId}/media`)}
                      >
                        <FontAwesomeIcon icon={faFolderOpen} size="xs" color="#0ea5e9" />
                        <Text fontSize="2xs" fontWeight="bold" noOfLines={1} flex={1}>
                          {item.courseName}
                        </Text>
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="2xs" />
                      </Flex>
                    </Tooltip>
                  </Box>

                  <Flex justify="space-between" align="center" pt={2} borderTop="1px dashed" borderColor={useColorModeValue('gray.100', 'gray.750')}>
                    <Text fontSize="2xs" color="gray.400">
                      {new Date(item.uploaded).toLocaleDateString()}
                    </Text>
                    <Button size="xs" variant="ghost" colorScheme="teal" leftIcon={<FontAwesomeIcon icon={faEye} />} onClick={() => setPreviewItem(item)}>
                      Preview
                    </Button>
                  </Flex>
                </Box>
              </MotionBox>
            );
          })}
        </SimpleGrid>
      ) : (
        /* List View */
        <VStack spacing={3} align="stretch">
          {filteredMedia.map((item) => {
            const typeInfo = getTypeIcon(item.type);
            return (
              <Flex
                key={`${item.courseId}-${item.id}`}
                bg={glassBg}
                p={3.5}
                borderRadius="xl"
                border={glassBorder}
                alignItems="center"
                gap={4}
                backdropFilter="blur(20px)"
                shadow="sm"
                _hover={{ y: -1, shadow: 'md', borderColor: '#0ea5e9' }}
                transition="all 0.2s"
              >
                {/* Type Icon Thumbnail */}
                <Flex
                  w="42px"
                  h="42px"
                  borderRadius="xl"
                  bg={typeInfo.bg}
                  color={typeInfo.color}
                  align="center"
                  justify="center"
                  fontSize="lg"
                  flexShrink={0}
                  cursor="pointer"
                  onClick={() => setPreviewItem(item)}
                >
                  <FontAwesomeIcon icon={typeInfo.icon} />
                </Flex>

                {/* Name and Tags */}
                <Box flex={1} minW="200px" overflow="hidden">
                  <Heading size="xs" color={titleColor} fontWeight="bold" noOfLines={1} title={item.name}>
                    {item.name}
                  </Heading>
                  <HStack spacing={2} mt={1}>
                    <Badge colorScheme={item.type === MediaType.Video ? 'purple' : item.type === MediaType.Image ? 'teal' : item.type === MediaType.Pdf ? 'red' : 'green'} borderRadius="full" fontSize="2xs">
                      {typeInfo.label}
                    </Badge>
                    <Text fontSize="2xs" color="gray.400">{new Date(item.uploaded).toLocaleDateString()}</Text>
                  </HStack>
                </Box>

                {/* Associated Course */}
                <Flex flex={1} minW="180px" align="center" gap={2}>
                  <Badge colorScheme="green" variant="subtle" px={2} py={1} borderRadius="md" fontSize="xs" fontWeight="bold" cursor="pointer" onClick={() => navigate(`/${item.courseId}/media`)} noOfLines={1}>
                    <FontAwesomeIcon icon={faFolderOpen} style={{ marginRight: '6px' }} />
                    {item.courseName}
                  </Badge>
                </Flex>

                {/* File Size */}
                <Text fontSize="xs" fontWeight="bold" color={titleColor} w="80px" textAlign="right">
                  {formatBytes(item.size)}
                </Text>

                {/* Action */}
                <IconButton
                  aria-label="Preview Asset"
                  icon={<FontAwesomeIcon icon={faEye} />}
                  size="sm"
                  colorScheme="green"
                  variant="ghost"
                  borderRadius="lg"
                  onClick={() => setPreviewItem(item)}
                />
              </Flex>
            );
          })}
        </VStack>
      )}

      {/* Full Asset Preview Modal */}
      <Modal isOpen={previewItem !== null} onClose={() => setPreviewItem(null)} size="3xl" isCentered>
        <ModalOverlay backdropFilter="blur(8px)" />
        <ModalContent borderRadius="2xl" overflow="hidden" bg={useColorModeValue('white', 'gray.900')}>
          <ModalHeader pb={2}>
            <Flex align="center" justify="space-between" pr={6}>
              <Box overflow="hidden">
                <Heading size="sm" color={titleColor} noOfLines={1}>{previewItem?.name}</Heading>
                <Text fontSize="xs" color="gray.400" mt={0.5}>Course: {previewItem?.courseName} ({previewItem?.courseCreator})</Text>
              </Box>
              <Badge colorScheme={previewItem?.type === MediaType.Video ? 'purple' : previewItem?.type === MediaType.Image ? 'teal' : 'red'} borderRadius="full" px={2.5} py={0.5} fontSize="xs">
                {previewItem ? getTypeIcon(previewItem.type).label : ''} • {formatBytes(previewItem?.size || 0)}
              </Badge>
            </Flex>
          </ModalHeader>
          <ModalCloseButton borderRadius="full" />
          <ModalBody p={6} pt={2}>
            {previewItem?.type === MediaType.Image && previewItem.fileUrl ? (
              <Flex justify="center" bg={useColorModeValue('gray.100', 'blackAlpha.700')} p={2} borderRadius="xl">
                <Image src={previewItem.fileUrl} alt={previewItem.name} maxH="500px" objectFit="contain" borderRadius="lg" />
              </Flex>
            ) : previewItem?.type === MediaType.Video && previewItem.fileUrl ? (
              <Box borderRadius="xl" overflow="hidden" bg="black">
                <video src={previewItem.fileUrl} controls autoPlay style={{ width: '100%', maxHeight: '500px' }} />
              </Box>
            ) : previewItem?.type === MediaType.Pdf && previewItem.fileUrl ? (
              <Flex direction="column" align="center" justify="center" p={8} bg={useColorModeValue('gray.50', 'gray.850')} borderRadius="xl" gap={4}>
                <FontAwesomeIcon icon={faFilePdf} size="4x" color="#ef4444" />
                <Text fontSize="sm" fontWeight="bold">{previewItem.name}</Text>
                <Button colorScheme="red" size="sm" as="a" href={previewItem.fileUrl} target="_blank" leftIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}>
                  Open Full PDF Document
                </Button>
              </Flex>
            ) : (
              <Flex direction="column" align="center" justify="center" p={12} bg={useColorModeValue('gray.50', 'gray.850')} borderRadius="xl" gap={3}>
                <FontAwesomeIcon icon={previewItem ? getTypeIcon(previewItem.type).icon : faHardDrive} size="3x" color="#0ea5e9" />
                <Text fontSize="sm" color="gray.500">Asset preview ready in course media workspace.</Text>
                <Button colorScheme="teal" size="sm" onClick={() => { const id = previewItem?.courseId; setPreviewItem(null); navigate(`/${id}/media`); }}>
                  Open in Course Media Manager
                </Button>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
